import React, { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, FileText, Loader2 } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  customerEmail: string;
  total: number;
  status: string;
  created: string;
}
export function SalesReport() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadInvoices();
  }, []);
  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Invoices'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      if (!resp || !Array.isArray(resp)) {
        setInvoices([]);
        return;
      }
      const loadedInvoices: Invoice[] = resp.map((item: any) => ({
        id: item.ID,
        invoiceNumber: item.Number || `INV-${item.ID}`,
        customerName: item.Customer_Name,
        customerEmail: item.Customer_Email,
        total: parseFloat(item.Total_Amount || 0),
        status: (() => {
          const totalAmount = parseFloat(item.Total_Amount || 0);
          const paidAmount = parseFloat(item.Paid_Amount || 0);
          const balance = totalAmount - paidAmount;
          if (paidAmount > 0 && balance > 0.01) {
            return 'Partially Paid';
          } else if (balance <= 0.01) {
            return 'Paid';
          }
          return 'Not Paid';
        })(),
        created: item.Added_Time
      }));
      setInvoices(loadedInvoices);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };
  const totalSales = invoices.reduce((sum, invoice) => sum + invoice.total, 0);
  const paidInvoices = invoices.filter(inv => inv.status === 'Paid');
  const partiallyPaidInvoices = invoices.filter(inv => inv.status === 'Partially Paid');
  const unpaidInvoices = invoices.filter(inv => inv.status === 'Not Paid');
  if (loading) {
    return <div className="p-6" data-id="element-1748">
        <div className="flex flex-col items-center justify-center py-12" data-id="element-1749">
          <Loader2 size={48} className="animate-spin text-blue-600 mb-4" data-id="element-1750" />
          <p className="text-slate-600" data-id="element-1751">Loading sales report...</p>
        </div>
      </div>;
  }
  return <div className="p-6" data-id="element-1752">
      <div className="mb-6" data-id="element-1753">
        <h2 className="text-2xl font-bold text-slate-800 mb-2" data-id="element-1754">Sales Report</h2>
        <p className="text-slate-600" data-id="element-1755">Overview of all invoiced rentals</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-1756">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg" data-id="element-1757">
          <div className="flex items-center justify-between mb-2" data-id="element-1758">
            <span className="text-blue-100 text-sm font-medium" data-id="element-1759">
              Total Sales
            </span>
            <DollarSign size={24} className="text-blue-200" data-id="element-1760" />
          </div>
          <div className="text-3xl font-bold" data-id="element-1761">
            $
            {totalSales.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
          </div>
          <div className="text-blue-100 text-xs mt-1" data-id="element-1762">
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="bg-white border border-green-200 rounded-lg p-6 shadow-sm" data-id="element-1763">
          <div className="flex items-center justify-between mb-2" data-id="element-1764">
            <span className="text-slate-600 text-sm font-medium" data-id="element-1765">
              Paid in Full
            </span>
            <TrendingUp size={20} className="text-green-600" data-id="element-1766" />
          </div>
          <div className="text-2xl font-bold text-green-600" data-id="element-1767">
            {paidInvoices.length}
          </div>
          <div className="text-slate-500 text-xs mt-1" data-id="element-1768">
            $
            {paidInvoices.reduce((sum, inv) => sum + inv.total, 0).toLocaleString('en-US', {
            minimumFractionDigits: 2
          })}
          </div>
        </div>

        <div className="bg-white border border-yellow-200 rounded-lg p-6 shadow-sm" data-id="element-1769">
          <div className="flex items-center justify-between mb-2" data-id="element-1770">
            <span className="text-slate-600 text-sm font-medium" data-id="element-1771">
              Partially Paid
            </span>
            <FileText size={20} className="text-yellow-600" data-id="element-1772" />
          </div>
          <div className="text-2xl font-bold text-yellow-600" data-id="element-1773">
            {partiallyPaidInvoices.length}
          </div>
          <div className="text-slate-500 text-xs mt-1" data-id="element-1774">
            $
            {partiallyPaidInvoices.reduce((sum, inv) => sum + inv.total, 0).toLocaleString('en-US', {
            minimumFractionDigits: 2
          })}
          </div>
        </div>

        <div className="bg-white border border-red-200 rounded-lg p-6 shadow-sm" data-id="element-1775">
          <div className="flex items-center justify-between mb-2" data-id="element-1776">
            <span className="text-slate-600 text-sm font-medium" data-id="element-1777">Not Paid</span>
            <FileText size={20} className="text-red-600" data-id="element-1778" />
          </div>
          <div className="text-2xl font-bold text-red-600" data-id="element-1779">
            {unpaidInvoices.length}
          </div>
          <div className="text-slate-500 text-xs mt-1" data-id="element-1780">
            $
            {unpaidInvoices.reduce((sum, inv) => sum + inv.total, 0).toLocaleString('en-US', {
            minimumFractionDigits: 2
          })}
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-id="element-1781">
        <div className="overflow-x-auto" data-id="element-1782">
          <table className="w-full" data-id="element-1783">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1784">
              <tr data-id="element-1785">
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1786">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1787">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1788">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1789">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1790">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-1791">
              {invoices.length === 0 ? <tr data-id="element-1792">
                  <td colSpan={5} className="px-6 py-12 text-center" data-id="element-1793">
                    <FileText size={48} className="text-slate-300 mx-auto mb-4" data-id="element-1794" />
                    <p className="text-slate-600 font-medium" data-id="element-1795">
                      No invoices found
                    </p>
                    <p className="text-sm text-slate-500 mt-1" data-id="element-1796">
                      Invoices will appear here once created
                    </p>
                  </td>
                </tr> : invoices.map(invoice => <tr key={invoice.id} className="hover:bg-slate-50 transition-colors" data-id="element-1797">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1798">
                      <span className="text-sm font-medium text-slate-800" data-id="element-1799">
                        {invoice.invoiceNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-1800">
                      <div className="text-sm text-slate-800 font-medium" data-id="element-1801">
                        {invoice.customerName}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-1802">
                        {invoice.customerEmail}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1803">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invoice.status === 'Paid' ? 'bg-green-100 text-green-800' : invoice.status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-1804">
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-1805">
                      <span className="text-sm font-semibold text-slate-800" data-id="element-1806">
                        $
                        {invoice.total.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1807">
                      <span className="text-sm text-slate-600" data-id="element-1808">
                        {new Date(invoice.created).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                      </span>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Total Summary Footer */}
      {invoices.length > 0 && <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-1809">
          <div className="flex items-center justify-between" data-id="element-1810">
            <div data-id="element-1811">
              <p className="text-sm text-blue-700 font-medium mb-1" data-id="element-1812">
                Total Invoiced Amount
              </p>
              <p className="text-xs text-blue-600" data-id="element-1813">
                Based on {invoices.length} invoice
                {invoices.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-right" data-id="element-1814">
              <p className="text-3xl font-bold text-blue-900" data-id="element-1815">
                $
                {totalSales.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}